import FreezingGraph from "./freezing-graph"
import { FreezingGraphAxis } from "./freezing-graph/axis"
import ForecastTableDay, { BOUNDARY_DETAIL } from "./day"
import { dayCellsDate } from "./cells"

function dayCellsDetail(cells) {
  // eslint-disable-next-line prefer-destructuring
  const mode = cells.days[0].dataset.mode
  if (mode === "h") return 2
  if (mode === "t") return 1
  return 0
}

export default class ForecastTableContent {
  constructor(table, data, elev, model, onDetailIncrease) {
    this.data = data
    this.level = elev
    this.model = model
    this.onDetailIncrease = onDetailIncrease
    this.todayIndex = 0
    this.operation = Promise.resolve()

    const { cells, graph } = data.parse(table)

    if (graph) {
      this.graphCell = graph
      this.graph = FreezingGraph(graph.firstElementChild)
      FreezingGraphAxis(table)
    }

    const details = cells.map((dayCells) => dayCellsDetail(dayCells))
    this.minDetail = Math.min(...details)

    let summaryCells = []
    let summaryI = 0
    let currentLength = 0

    this.size = 0
    this.days = cells.map((parsedDayCells) => {
      const dayCells = { ...parsedDayCells }
      const size = dayCells.time.length

      if (dayCells.summary) {
        summaryCells = summaryCells.concat(dayCells.summary)
        delete dayCells.summary
      }

      let summaryCell = summaryCells.at(summaryI)
      if (summaryCell) {
        if (summaryCell.colSpan <= currentLength) {
          summaryI++
          summaryCell = summaryCells.at(summaryI)
          currentLength = 0
        }
        currentLength += size
      }

      if (dayCells.tabs) {
        this.tabsCell = dayCells.tabs.at(0)
        delete dayCells.tabs
      }

      const date = dayCellsDate(dayCells)
      const detail = dayCellsDetail(dayCells)
      const day = new ForecastTableDay({
        table: this,
        date,
        minDetail: this.minDetail,
        summaryCell,
      })
      day.setCells(this.model, this.level, detail, dayCells)
      this.size += size
      return day
    })

    this.summaryCells = summaryCells
  }

  enqueue(operation) {
    const result = this.operation.then(operation, operation)
    this.operation = result.catch(() => {})
    return result
  }

  dayOffset(day) {
    let offset = 0
    for (let i = 0; i < this.days.length; i++) {
      if (this.days[i] === day) break

      offset += this.days[i].size
    }
    return offset
  }

  todayOffsetLeft() {
    return this.days[this.todayIndex].header.root.offsetLeft
  }

  updateFreezingGraph(day, offset, removeCount, newData) {
    if (!this.graph) return

    const oldSize = day.size
    const [groups, points] = newData
    const addCount = points[0]?.length ?? groups[0]?.length ?? 0
    const start = this.dayOffset(day) + offset
    const border = oldSize === 0 ? 1 : 0
    const delta = addCount - removeCount
    const deltaWidth = delta * 38 + border
    const dayEndLine = start - offset + oldSize

    this.graphCell.colSpan += delta
    this.graph.updateSVGWidth(this.graph.width + deltaWidth)
    this.graph.updateCells(start, removeCount, groups)()
    this.graph.updateFreezingLine(start, removeCount, points)()

    if (oldSize === 0) {
      this.graph.addDayEndLine(deltaWidth)
    } else if (delta !== 0) {
      this.graph.updateDayEndLines(dayEndLine, delta)()
    }
  }

  dayChanged(day, oldSize, newSize, freezingData) {
    const delta = newSize - oldSize
    this.size += delta

    if (this.graph && freezingData) {
      this.updateFreezingGraph(day, 0, oldSize, freezingData)
    }

    if (this.tabsCell) this.tabsCell.colSpan += delta
  }

  async updateDays(indexes, model = this.model, level = this.level) {
    const uniqueIndexes = [...new Set(indexes)].sort((a, b) => a - b)
    const states = await Promise.all(
      uniqueIndexes.map(async (index) => {
        const day = this.days[index]
        const nextDay = this.days[index + 1]
        const previousExpanded = index > 0 && this.days[index - 1].expanded
        const state = await day.prepareState(
          model,
          level,
          previousExpanded,
          nextDay?.expanded,
          nextDay?.hasEarlyNight({ model, level }),
        )
        return [day, state]
      }),
    )

    states.forEach(([day, state]) => day.applyState(state, model, level))
  }

  changeDay(day, detail) {
    return this.enqueue(async () => {
      const index = this.days.indexOf(day)
      const indexes = []
      const ensure = []
      const currentDay = this.days[index]
      const expanded = detail > 0
      if (detail === currentDay.detail) return

      if (index > 0) {
        indexes.push(index - 1)

        const prevDay = this.days[index - 1]
        if (expanded && !prevDay.expanded) {
          ensure.push({ day: this.days[index - 1], detail: BOUNDARY_DETAIL, optional: true })
        }
      }

      indexes.push(index)
      ensure.push({ day: currentDay, detail })

      if (index < this.days.length - 1) {
        indexes.push(index + 1)

        const nextDay = this.days[index + 1]
        if (expanded && !nextDay.expanded) {
          ensure.push({ day: this.days[index + 1], detail: BOUNDARY_DETAIL, optional: true })
        }
      }

      await this.ensureMultiple(this.model, this.level, ensure)

      const previousDetail = currentDay.detail
      currentDay.detail = detail

      try {
        await this.updateDays(indexes)
      } catch (error) {
        currentDay.detail = previousDetail
        throw error
      }

      if (detail > previousDetail) this.onDetailIncrease?.(detail, this.level)
    })
  }

  changeLevel(level) {
    return this.enqueue(async () => {
      if (level === this.level) return

      await this.fetchAll(this.model, level)
      await this.updateDays(
        this.days.map((_, index) => index),
        this.model,
        level,
      )
      this.level = level
    })
  }

  changeModel(model) {
    return this.enqueue(async () => {
      if (model === this.model) return

      await this.fetchAll(model, this.level)
      await this.updateDays(
        this.days.map((_, index) => index),
        model,
        this.level,
      )
      this.model = model
    })
  }

  changeAllDetails(delta) {
    return this.enqueue(async () => {
      const changes = this.days.map((day) => {
        const detail = Math.max(day.minDetail, Math.min(day.detail + delta, day.header.maxDetail))
        return { day, detail, previousDetail: day.detail }
      })

      await this.ensureMultiple(this.model, this.level, changes)

      changes.forEach((_, index) => {
        changes[index].day.detail = changes[index].detail
      })

      try {
        await this.updateDays(this.days.map((_, index) => index))
      } catch (error) {
        changes.forEach((_, index) => {
          changes[index].day.detail = changes[index].previousDetail
        })
        throw error
      }

      changes.forEach(({ detail, previousDetail }) => {
        if (detail > previousDetail) this.onDetailIncrease?.(detail, this.level)
      })
    })
  }

  expandAll() {
    return this.changeAllDetails(1)
  }

  collapseAll() {
    return this.changeAllDetails(-1)
  }

  back() {
    return this.enqueue(() => this.loadPast())
  }

  async loadPast() {
    const firstDay = this.days[0]
    const needsCompletion = firstDay.isIncomplete()
    const detail = 0
    let cellsAdded = 0
    let completeFirstDay = false

    const data = await this.data.fetchPast(this.model, this.level, firstDay.date, needsCompletion)
    if (!data.cells) return data

    const { cells } = data
    delete data.cells
    if (needsCompletion) this.completedDate = firstDay.date

    for (let i = 0; i < cells.length; i++) {
      const dayCells = cells[i]

      // TODO: move completion logic to the backend and check date here instead
      if (needsCompletion && i === cells.length - 1) {
        firstDay.setCells(this.model, this.level, detail, dayCells)
        if (firstDay.detail === detail) completeFirstDay = true
      } else {
        const day = new ForecastTableDay({
          table: this,
          date: dayCellsDate(dayCells),
          minDetail: detail,
        })
        this.days.splice(i, 0, day)

        day.insert(this.model, this.level, detail, dayCells, firstDay)
        this.todayIndex++
        cellsAdded += dayCells.time.length
      }
    }

    if (completeFirstDay) await this.updateDays([this.days.indexOf(firstDay)])

    if (this.summaryCells.length) {
      if (!this.pastSummaryCell) {
        this.pastSummaryCell = document.createElement("td")
        this.pastSummaryCell.className = "forecast-table__cell"
        const first = this.summaryCells[0]
        first.parentNode.insertBefore(this.pastSummaryCell, first)
        cellsAdded -= 1
      }
      this.pastSummaryCell.colSpan += cellsAdded
    }

    return data
  }

  async fetchAll(model, level) {
    const missing = this.days.filter((d) => !d.cellsFor({ model, level }))
    const future = missing.filter((d) => this.days.indexOf(d) >= this.todayIndex)

    if (future.length) {
      const dates = future.map((d) => `${d.date}:${d.detail + 1}`)
      const cells = await this.data.fetch(model, level, dates)
      for (const dayCells of cells) {
        const date = dayCellsDate(dayCells)
        const day = this.days.find((d) => d.date === date)
        if (!day) continue

        day.setCells(model, level, day.detail, dayCells)
      }

      const unavailable = future.filter((d) => !d.cellsFor({ model, level }))
      if (unavailable.length) {
        throw new Error(
          `Missing data for ${unavailable.map((d) => d.date).join(", ")} at ${model}/${level}`,
        )
      }
    }

    if (missing.length > future.length) await this.fetchAllPast(model, level)
  }

  // The forecast endpoint has no hindcast days, so they're refetched the way back() loaded them
  async fetchAllPast(model, level) {
    const pastDays = this.days.slice(0, this.todayIndex)
    const firstDate = pastDays[0].date
    let { date } = this.days[this.todayIndex]
    let complete = this.completedDate === date

    while (date > firstDate) {
      // Each response holds the days before the previous one's, so the requests run in turn
      // eslint-disable-next-line no-await-in-loop
      const data = await this.data.fetchPast(model, level, date, complete)
      if (!data.cells || !data.cells.length) break

      data.cells.forEach((dayCells) => {
        const day = this.days.find((d) => d.date === dayCellsDate(dayCells))
        if (day) day.setCells(model, level, day.detail, dayCells)
      })

      date = dayCellsDate(data.cells[0])
      complete = false
    }
  }

  async ensureMultiple(model, level, requests) {
    const missing = requests.filter(({ day, detail }) => !day.cellsFor({ model, level, detail }))
    if (!missing.length) return

    const dates = missing.map(({ day, detail }) => `${day.date}:${detail + 1}`)
    try {
      const cells = await this.data.fetch(model, level, dates)
      cells?.forEach((dayCells) => {
        const date = dayCellsDate(dayCells)
        const request = missing.find((r) => r.day.date === date)
        if (request) request.day.setCells(model, level, request.detail, dayCells)
      })
    } catch (e) {
      // console.log(e)
    }

    const unavailable = missing.filter(
      ({ day, detail, optional }) => !optional && !day.cellsFor({ model, level, detail }),
    )
    if (unavailable.length) {
      throw new Error(
        `Missing data for ${unavailable
          .map(({ day, detail }) => `${day.date}/${detail}`)
          .join(", ")} at ${model}/${level}`,
      )
    }
  }
}
